import {announce} from '@github-ui/aria-live'
import type {
  CopilotChatReference,
  DiscussionReference,
  IssueReference,
  PullRequestReference,
} from '@github-ui/copilot-chat-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {useCallback, useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent} from 'react'

import {
  githubReference,
  implicitAttachmentIcon,
  implicitAttachmentKey,
  implicitAttachmentUrl,
  REFERENCE_TYPE_ANNOUNCE_LABEL,
  type ReferenceType,
} from './attachment-refs'
import {fetchPastedReference} from './fetch-pasted-reference'
import {parsePastedGitHubReference, type ParsedGitHubReference} from './parse-github-reference-url'
import type {TrackedAttachment} from './process-uploaded-files'
import {isAttachmentInScope} from './attachment-scope'

export const PASTED_REFERENCE_ID_PREFIX = 'pasted-reference:'

// Matches http(s) URLs in pasted text. Stops at whitespace or characters that
// commonly follow but aren't part of a URL (angle brackets); trailing
// punctuation is stripped separately by the caller.
const URL_REGEX = /https?:\/\/[^\s<>]+/gi

// Trailing characters that commonly follow a URL in prose (`.`, `,`, closing
// bracket, quote, etc.) but shouldn't be treated as part of the URL itself.
const URL_TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/g

/**
 * Returns the set of canonical GitHub reference URLs currently present in the
 * given text. Each URL-like substring is parsed and normalized (e.g. numeric
 * padding stripped, trailing slashes dropped), so callers can compare by exact
 * canonical identity rather than substring — `/issues/5` and `/issues/50` are
 * distinct here, while `/issues/005` collapses to `/issues/5`.
 */
function collectCanonicalReferenceUrls(text: string): Set<string> {
  const canonicalUrls = new Set<string>()
  const matches = text.match(URL_REGEX)
  if (!matches) return canonicalUrls
  for (const rawMatch of matches) {
    const cleaned = rawMatch.replace(URL_TRAILING_PUNCTUATION, '')
    const parsed = parsePastedGitHubReference(cleaned, ssrSafeLocation.origin)
    if (parsed) canonicalUrls.add(parsed.url)
  }
  return canonicalUrls
}

function readClipboardText(clipboardData: DataTransfer): string {
  const stringItem = Array.from(clipboardData.items ?? []).find(
    item => item.kind === 'string' && (item.type === 'text' || item.type === 'text/plain'),
  )
  return stringItem ? clipboardData.getData(stringItem.type) : clipboardData.getData('text/plain')
}

const REFERENCE_TYPE_BY_ENDPOINT_TYPE: Partial<Record<CopilotChatReference['type'], ReferenceType>> = {
  'pull-request': 'pr',
  issue: 'issue',
  discussion: 'discussion',
}

function referenceDetails(reference: PullRequestReference | IssueReference | DiscussionReference): {
  title?: string
  state?: string
  draft?: boolean
} {
  if (reference.type === 'pull-request') return {title: reference.title, state: reference.state, draft: reference.draft}
  if (reference.type === 'issue') return {title: reference.title, state: reference.state}
  return {title: reference.title, state: reference.state}
}

function buildTrackedAttachment(id: string, ahp: MessageAttachment): TrackedAttachment {
  return {
    token: {id, label: ahp.label, icon: implicitAttachmentIcon(ahp), url: implicitAttachmentUrl(ahp)},
    ahp,
  }
}

export interface UsePasteReferencesOptions {
  activeImplicitAttachments: MessageAttachment[]
  scopeOwner?: string | null
}

export interface UsePasteReferencesResult {
  pastedReferences: TrackedAttachment[]
  handlePaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void
  /**
   * Scans the pasted clipboard text for GitHub issue/PR/discussion URLs and
   * attaches each unique one as a reference. Does not call `preventDefault`,
   * so the pasted text is still inserted into the composer verbatim.
   */
  tryAttachReference: (event: ClipboardEvent<HTMLTextAreaElement>) => void
  removePastedReference: (id: string) => void
  clearPastedReferences: (ids: string[]) => void
  selectSubmittedPastedReferences: (submitted: TrackedAttachment[]) => TrackedAttachment[]
  /**
   * Programmatically attach a same-origin GitHub reference URL (issue, pull
   * request, or discussion). Shares the same deduplication, AHP conversion,
   * enrichment, and announcement pipeline as `tryAttachReference` but accepts a
   * URL string instead of a clipboard event. Silently no-ops for invalid,
   * duplicate, or non-parseable URLs.
   */
  attachReferenceUrl: (url: string) => void
  /**
   * Drop any pasted references whose canonical URL is no longer present in
   * the composer text. Intended to be called on every text change so editing
   * a pasted URL (any character added or removed that changes its canonical
   * form) detaches the corresponding chip, mirroring the immersive-chat
   * mention behaviour.
   */
  reconcileFromText: (text: string) => void
}

export function usePasteReferences({
  activeImplicitAttachments,
  scopeOwner,
}: UsePasteReferencesOptions): UsePasteReferencesResult {
  const scopeOwnerRef = useRef(scopeOwner)
  const listRef = useRef<TrackedAttachment[]>([])
  const [pastedReferences, setPastedReferences] = useState<TrackedAttachment[]>([])
  const commit = useCallback((next: TrackedAttachment[]) => {
    listRef.current = next
    setPastedReferences(next)
  }, [])

  // Tracks pasted-reference ids whose URL has been observed in the composer
  // text at least once. The reconcile effect only removes a reference once its
  // URL has been *seen* and then *disappears* — a fresh paste commits the ref
  // before the browser has inserted the pasted text into the textarea, so
  // without this the reconcile would immediately drop the just-added chip.
  const confirmedIdsRef = useRef<Set<string>>(new Set())

  // Only an explicit removal drops a reference from an in-flight submission.
  // Text reconciliation may remove the visible chip while preserving the
  // snapshot already associated with that submission.
  const userRemovedIdsRef = useRef<Set<string>>(new Set())

  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())
  const activeImplicitAttachmentsRef = useRef(activeImplicitAttachments)
  useEffect(() => {
    activeImplicitAttachmentsRef.current = activeImplicitAttachments
  }, [activeImplicitAttachments])

  useEffect(() => {
    const controllers = abortControllersRef.current
    return () => {
      for (const controller of controllers.values()) controller.abort()
      controllers.clear()
    }
  }, [])

  const abortPasteAttempt = useCallback((id: string) => {
    const controller = abortControllersRef.current.get(id)
    if (!controller) return
    controller.abort()
    abortControllersRef.current.delete(id)
  }, [])

  const removePastedReference = useCallback(
    (id: string) => {
      abortPasteAttempt(id)
      confirmedIdsRef.current.delete(id)
      userRemovedIdsRef.current.add(id)
      commit(listRef.current.filter(item => item.token.id !== id))
    },
    [abortPasteAttempt, commit],
  )
  useLayoutEffect(() => {
    scopeOwnerRef.current = scopeOwner
    for (const item of listRef.current) {
      if (!isAttachmentInScope(item.ahp, scopeOwner)) removePastedReference(item.token.id)
    }
  }, [scopeOwner, removePastedReference])

  const clearPastedReferences = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return
      for (const id of ids) {
        abortPasteAttempt(id)
        confirmedIdsRef.current.delete(id)
      }
      const removed = new Set(ids)
      commit(listRef.current.filter(item => !removed.has(item.token.id)))
    },
    [abortPasteAttempt, commit],
  )

  const selectSubmittedPastedReferences = useCallback((submitted: TrackedAttachment[]) => {
    const live = new Map(listRef.current.map(item => [item.token.id, item] as const))
    return submitted
      .filter(
        item => !userRemovedIdsRef.current.has(item.token.id) && isAttachmentInScope(item.ahp, scopeOwnerRef.current),
      )
      .map(item => live.get(item.token.id) ?? item)
  }, [])

  const enrich = useCallback(
    (id: string, parsed: ParsedGitHubReference) => {
      const controller = new AbortController()
      abortControllersRef.current.set(id, controller)

      void (async () => {
        try {
          const reference = await fetchPastedReference(parsed.url, controller.signal)
          if (controller.signal.aborted) return
          if (!reference) return

          const mappedType = REFERENCE_TYPE_BY_ENDPOINT_TYPE[reference.type]
          const number = 'number' in reference ? reference.number : undefined
          if (mappedType !== parsed.type || number !== parsed.number) return

          const {title, state, draft} = referenceDetails(
            reference as PullRequestReference | IssueReference | DiscussionReference,
          )
          const enrichedAhp = githubReference(parsed.type, parsed.number, parsed.url, title, state, draft)
          if (!enrichedAhp) return

          if (!listRef.current.some(item => item.token.id === id)) return
          commit(listRef.current.map(item => (item.token.id === id ? buildTrackedAttachment(id, enrichedAhp) : item)))
        } catch (error) {
          void error
        } finally {
          abortControllersRef.current.delete(id)
        }
      })()
    },
    [commit],
  )

  const tryAttachReference = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>): void => {
      const clipboardData = event.clipboardData
      if (!clipboardData) return
      if ((clipboardData.files?.length ?? 0) > 0) return

      const text = readClipboardText(clipboardData)
      if (!text) return

      // Extract every URL-like substring from the paste. We don't require the
      // paste to be *only* a URL — a message that mentions one or more GitHub
      // URLs still gets each URL turned into a reference, while the pasted
      // text is left untouched so it appears in the composer verbatim.
      const matches = text.match(URL_REGEX)
      if (!matches) return

      const seen = new Set<string>()
      const parsedRefs: ParsedGitHubReference[] = []
      for (const rawMatch of matches) {
        // Strip common trailing punctuation that isn't part of the URL itself.
        const cleaned = rawMatch.replace(URL_TRAILING_PUNCTUATION, '')
        const parsed = parsePastedGitHubReference(cleaned, ssrSafeLocation.origin)
        if (!parsed) continue
        if (scopeOwnerRef.current !== undefined && parsed.owner.toLowerCase() !== scopeOwnerRef.current?.toLowerCase())
          continue
        if (seen.has(parsed.url)) continue
        seen.add(parsed.url)
        if (activeImplicitAttachmentsRef.current.some(attachment => implicitAttachmentKey(attachment) === parsed.url)) {
          continue
        }
        if (listRef.current.some(item => implicitAttachmentUrl(item.ahp) === parsed.url)) continue
        parsedRefs.push(parsed)
      }

      if (parsedRefs.length === 0) return

      const newItems: Array<{item: TrackedAttachment; parsed: ParsedGitHubReference}> = []
      for (const parsed of parsedRefs) {
        const id = `${PASTED_REFERENCE_ID_PREFIX}${crypto.randomUUID()}`
        const fallbackAhp = githubReference(parsed.type, parsed.number, parsed.url)
        if (!fallbackAhp) continue
        newItems.push({item: buildTrackedAttachment(id, fallbackAhp), parsed})
      }
      if (newItems.length === 0) return

      commit([...listRef.current, ...newItems.map(({item}) => item)])
      for (const {item, parsed} of newItems) {
        announce(`Attached ${REFERENCE_TYPE_ANNOUNCE_LABEL[parsed.type]} #${parsed.number}`)
        enrich(item.token.id, parsed)
      }
    },
    [commit, enrich],
  )

  /**
   * Attach a reference URL programmatically. Shares deduplication, AHP
   * conversion, enrichment, announcement, and cleanup with `tryAttachReference`.
   */
  const attachReferenceUrl = useCallback(
    (url: string) => {
      const parsed = parsePastedGitHubReference(url, ssrSafeLocation.origin)
      if (!parsed) return
      if (scopeOwnerRef.current !== undefined && parsed.owner.toLowerCase() !== scopeOwnerRef.current?.toLowerCase())
        return

      const matchesImplicit = activeImplicitAttachmentsRef.current.some(
        attachment => implicitAttachmentKey(attachment) === parsed.url,
      )
      if (matchesImplicit) return

      const isDuplicate = listRef.current.some(item => implicitAttachmentUrl(item.ahp) === parsed.url)
      if (isDuplicate) return

      const id = `${PASTED_REFERENCE_ID_PREFIX}${crypto.randomUUID()}`
      const fallbackAhp = githubReference(parsed.type, parsed.number, parsed.url)
      if (!fallbackAhp) return

      commit([...listRef.current, buildTrackedAttachment(id, fallbackAhp)])
      announce(`Attached ${REFERENCE_TYPE_ANNOUNCE_LABEL[parsed.type]} #${parsed.number}`)
      enrich(id, parsed)
    },
    [commit, enrich],
  )

  // Removes any pasted reference whose canonical URL is no longer present in
  // the composer text. Comparison is by canonical identity (not substring), so
  // editing `/issues/5` into `/issues/50` correctly detaches the chip even
  // though the old value is still a substring, and normalized forms like
  // `/issues/005` are treated as equivalent to `/issues/5`. A ref is only
  // eligible for removal after its URL has been observed in the text at least
  // once — this covers the paste ordering where the ref commits before the
  // browser has inserted the pasted text, so an immediate reconcile would
  // otherwise drop the just-added chip.
  const reconcileFromText = useCallback(
    (text: string) => {
      const canonicalUrls = collectCanonicalReferenceUrls(text)
      const toRemove: string[] = []
      for (const item of listRef.current) {
        const url = implicitAttachmentUrl(item.ahp)
        if (!url) continue
        const present = canonicalUrls.has(url)
        if (present) {
          confirmedIdsRef.current.add(item.token.id)
        } else if (confirmedIdsRef.current.has(item.token.id)) {
          toRemove.push(item.token.id)
        }
      }
      if (toRemove.length > 0) clearPastedReferences(toRemove)
    },
    [clearPastedReferences],
  )

  return {
    pastedReferences,
    handlePaste: tryAttachReference,
    tryAttachReference,
    removePastedReference,
    clearPastedReferences,
    selectSubmittedPastedReferences,
    attachReferenceUrl,
    reconcileFromText,
  }
}
